import { createStyles, makeStyles, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React, { MouseEvent, useState } from 'react';
import { Info } from 'react-feather';
import { useBooleanState } from 'react-use-object-state';
import { EntityClass } from '../../../../../types';
import { JupiterTheme } from '../../../../theme/types';
import { useSnackbarNotification } from '../../../notifications/SnackbarNotificationContext';
import DragDropFileUpload from '../../../util/DragDropFileUpload';
import ClassTiles from './ClassTiles';
import EntityUploadModal from './EntityUploadModal';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    container: {
      display: 'grid',
      gridAutoFlow: 'row',
      gridTemplateColumns: 'repeat(auto-fit, 200px);',
      gridGap: theme.spacing(2),
      height: `calc(100vh - 70px)`,
      padding: theme.spacing(3, 0),
    },
    upload: {
      gridRow: '1 / 3',
      gridColumn: '1 / 3',
    },
    uploadContainer: {
      height: '100%',
      display: 'grid',
      gridTemplateRows: '1fr 1fr auto',
      justifyItems: 'center',
    },
    uploadContainerPending: {
      gridTemplateRows: '1fr',
    },
    dragDropChildren: {
      color: theme.palette.coolGrey[500],
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      paddingBottom: theme.spacing(0.5),
      '&:before': {
        background: theme.palette.primary.main,
        borderRadius: 3,
        bottom: -2,
        content: `''`,
        display: 'block',
        height: 5,
        position: 'absolute',
        width: 0,
      },
      '&:hover': {
        color: theme.palette.primary.main,
        transition: '0.25s ease-in-out',
        '&:before': {
          transition: '.2s ease-out',
          width: '100%',
        },
      },
    },
    primaryTitle: {
      alignSelf: 'end',
    },
    secondaryText: {
      alignSelf: 'baseline',
    },
    infoText: {
      fontSize: 14,
      marginLeft: theme.spacing(0.5),
    },
    infoIcon: {
      display: 'inline-block',
    },
  })
);

export interface ClassTileContainerProps {
  onSelectClass: (selectedClass: EntityClass, className: string) => void;
  onBulkUpload: (uploadData: File, scope?: string) => Promise<void>;
}
export const useClassTileContainer = ({
  onBulkUpload,
}: Omit<ClassTileContainerProps, 'onSelectClass'>) => {
  const notification = useSnackbarNotification();
  const pendingFileUpload = useBooleanState(false);
  const [uploadedFile, setUploadedFile] = useState<File>();
  const openScopeModal = useBooleanState(false);

  const handleFileUpload = async (files: File[]) => {
    if (files.length > 0) {
      openScopeModal.setTrue();
      setUploadedFile(files[0]);
    }
  };

  const handleScopeModalSubmit = (scope: string) => {
    if (uploadedFile) {
      pendingFileUpload.setTrue();
      return onBulkUpload(uploadedFile, scope);
    }
    notification.error({
      message: 'Please upload a file to initiate the bulk upload process.',
    });
  };

  const handleMoreInfoClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    window.open(
      'https://support.jupiterone.io/hc/en-us/articles/360049348293',
      '_blank',
      'noopener,noreferrer'
    );
  };
  return {
    pendingFileUpload: pendingFileUpload.state,
    modalOpen: openScopeModal.state,
    closeModal: openScopeModal.setFalse,
    handleFileUpload,
    handleScopeModalSubmit,
    handleMoreInfoClick,
  };
};
const ClassTileContainer = ({
  onSelectClass,
  onBulkUpload,
}: ClassTileContainerProps) => {
  const classes = useStyles();
  const {
    pendingFileUpload,
    modalOpen,
    closeModal,
    handleFileUpload,
    handleScopeModalSubmit,
    handleMoreInfoClick,
  } = useClassTileContainer({
    onBulkUpload,
  });

  return (
    <div className={classes.container} data-testid="ClassTileContainer">
      <DragDropFileUpload
        accept=".json,.yaml,.yml"
        classes={{
          root: classes.upload,
          container: clsx(
            classes.uploadContainer,
            pendingFileUpload && classes.uploadContainerPending
          ),
          primaryText: classes.primaryTitle,
          secondaryText: classes.secondaryText,
        }}
        multiple={false}
        onDrop={handleFileUpload}
        primaryText="Bulk upload from a JSON or YAML file"
        secondaryText="Drag 'n' drop your file here, or click to select a file"
        pending={pendingFileUpload}
        pendingText="Uploading your assets..."
      >
        <div onClick={handleMoreInfoClick} className={classes.dragDropChildren}>
          <Info size={14} className={classes.infoIcon}></Info>
          <Typography display="inline" className={classes.infoText}>
            Read details on the expected file format
          </Typography>
        </div>
      </DragDropFileUpload>
      <ClassTiles onSelectClass={onSelectClass} disabled={pendingFileUpload} />
      <EntityUploadModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleScopeModalSubmit}
      ></EntityUploadModal>
    </div>
  );
};
export default ClassTileContainer;
